import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Consultation, Destination, DestinationHealthInfo } from "@/types";
import { parseDestinations, getUrgencyInfo } from "@/utils/dashboardHelpers";
import { formatDate } from "@/utils/formatters";
import { HealthRecommendationsPanel } from "./HealthRecommendationsPanel";

interface PendingConsultationCardProps {
  consultation: Consultation;
  showRecommendations: boolean;
  loadingDestinations: boolean;
  destinationInfoMap: Record<string, DestinationHealthInfo>;
  expandedCountries: Record<string, boolean>;
  onToggleRecommendations: () => void;
  onToggleCountry: (country: string) => void;
}

export function PendingConsultationCard({
  consultation,
  showRecommendations,
  loadingDestinations,
  destinationInfoMap,
  expandedCountries,
  onToggleRecommendations,
  onToggleCountry,
}: PendingConsultationCardProps) {
  const navigate = useNavigate();
  const [showTravelerMenu, setShowTravelerMenu] = useState(false);
  const urgencyInfo = getUrgencyInfo(consultation.departureDate);

  const getPriorityTab = () => {
    // Check if all forms are completed
    const isCompleted =
      consultation.formsStatus.completed > 0 &&
      consultation.formsStatus.pending === 0 &&
      (consultation.formsStatus.underReview ||
        consultation.formsStatus.under_review ||
        0) === 0;

    if (isCompleted) {
      return (
        <div className="absolute top-0 right-0 px-3 py-1.5 rounded-tr-2xl rounded-bl-xl font-bold text-xs bg-success text-white">
          Completed
        </div>
      );
    }

    const tabStyles = {
      overdue: "bg-error text-white",
      urgent: "bg-warning text-white",
      soon: "bg-warning text-white",
      normal: "bg-warning text-white",
    };

    const labels = {
      overdue: "Overdue",
      urgent: "Urgent",
      soon: "Urgent",
      normal: "Urgent",
    };

    return (
      <div
        className={`absolute top-0 right-0 px-3 py-1.5 rounded-tr-2xl rounded-bl-xl font-bold text-xs ${
          tabStyles[urgencyInfo.level as keyof typeof tabStyles]
        }`}
      >
        {labels[urgencyInfo.level as keyof typeof labels]}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 relative">
      {/* Priority Tab */}
      {getPriorityTab()}

      <div className="flex items-center mb-3 pr-20">
        {/* User Icon and Name */}
        <div className="p-2.5 bg-gray-100 rounded-full mr-3">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {consultation.travelers && consultation.travelers.length > 0
              ? consultation.travelers.map((t, idx) => (
                  <span key={t.traveler_id}>
                    {t.first_name} {t.last_name}
                    {idx < consultation.travelers.length - 1 && ", "}
                  </span>
                ))
              : "No Travelers"}
          </h3>
          <p className="text-sm text-gray-600 mt-0.5">
            Consultation #{consultation.consultationNumber}
          </p>
        </div>
      </div>

      {/* Collapsible Consultation Details */}
      <details className="group mb-3">
        <summary className="cursor-pointer list-none flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          <span className="text-sm font-semibold text-gray-700">
            View Details
          </span>
          <svg
            className="w-4 h-4 text-gray-600 transition-transform group-open:rotate-180"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </summary>
        {/* Consultation Details */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2 mt-1.5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg
                className="w-5 h-5 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">
                Travel Destination
              </div>
              <div className="text-base font-semibold text-gray-900">
                {parseDestinations(consultation.destinations)}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 bg-success-bg rounded-lg">
              <svg
                className="w-5 h-5 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">
                Travel Dates
              </div>
              <div className="text-base font-semibold text-gray-900">
                {formatDate(consultation.departureDate)} -{" "}
                {formatDate(consultation.returnDate)}
              </div>
            </div>
          </div>
        </div>
      </details>

      {/* Action Buttons Section */}
      <div>
        {consultation.travelers && consultation.travelers.length > 0 ? (
          <>
            {/* Review Forms Button with Dropdown */}
            <div className="relative">
              {consultation.travelers.length === 1 ? (
                <Link
                  to={`/travelers/${consultation.travelers[0].traveler_id}/review`}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Review Form
                </Link>
              ) : (
                <details className="group">
                  <summary className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer list-none">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      Review Forms ({consultation.travelers.length} Travelers)
                    </span>
                    <svg
                      className="w-4 h-4 transition-transform group-open:rotate-180"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>

                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-10 overflow-hidden">
                    {consultation.travelers.map((traveler, index) => {
                      const statusColors = {
                        submitted: "hover:bg-teal-50 text-teal-700",
                        under_review: "hover:bg-yellow-50 text-yellow-700",
                        complete: "hover:bg-green-50 text-green-700",
                        completed: "hover:bg-green-50 text-green-700",
                      };

                      const statusLabels = {
                        submitted: "Start Review",
                        under_review: "Continue",
                        complete: "View",
                        completed: "View",
                      };

                      const hoverColor =
                        statusColors[
                          traveler.form_status as keyof typeof statusColors
                        ] || "hover:bg-teal-50 text-teal-700";
                      const label =
                        statusLabels[
                          traveler.form_status as keyof typeof statusLabels
                        ] || "Review";

                      return (
                        <Link
                          key={traveler.traveler_id}
                          to={`/travelers/${traveler.traveler_id}/review`}
                          className={`flex items-center justify-between gap-3 px-4 py-2.5 ${hoverColor} transition-colors border-b border-gray-100 last:border-b-0`}
                        >
                          <span className="flex items-center gap-2 flex-1">
                            <svg
                              className="w-4 h-4 flex-shrink-0 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            <span className="font-semibold text-gray-900">
                              {traveler.first_name} {traveler.last_name}
                            </span>
                          </span>
                          <span className="text-xs font-bold bg-gray-100 px-2 py-1 rounded">
                            {label}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          </>
        ) : (
          <div className="px-5 py-2.5 bg-gray-300 text-gray-600 font-semibold rounded-lg text-center cursor-not-allowed">
            No Information Available
          </div>
        )}
      </div>
    </div>
  );
}
